import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import PublicRoute from '../components/auth/PublicRoute';
import { useAuthStore } from '../store';
import { EMPLOYER_PERMISSIONS, getDefaultAllowedPath } from '../utils/rbac';

// Auth Pages
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import ContactVerificationPage from '../pages/auth/ContactVerificationPage';
import KYCVerificationPage from '../pages/auth/KYCVerificationPage';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '../pages/auth/ResetPasswordPage';
import LegalPage from '../pages/legal/LegalPage';

// Dashboard Pages
import DashboardLayout from '../components/layout/DashboardLayout';
import DashboardOverview from '../pages/dashboard/DashboardOverview';
import ReportsPage from '../pages/reports/ReportsPage';
import ProfilePage from '../pages/profile/ProfilePage';
import ConfigPage from '../pages/profile/ConfigPage'; // <--- 1. ADD THIS IMPORT
import RolesPage from '../pages/admin/RolesPage';
import UsersPage from '../pages/admin/UsersPage';
import EmployerGuidePage from '../pages/help/EmployerGuidePage';

// Jobs Pages
import JobsOverview from '../pages/jobs/JobsOverview';
import CreateJobPage from '../pages/jobs/CreateJobPage';
import JobDetailsPage from '../pages/jobs/JobDetailsPage';
import JobMatchesPage from '../pages/jobs/JobMatchesPage';

// Candidates Pages
import CandidatesOverview from '../pages/candidates/CandidatesOverview';
import CandidateProfile from '../pages/candidates/CandidateProfile';
import MatchingBands from '../pages/candidates/MatchingBands';

// Interviews Pages
import InterviewsOverview from '../pages/interviews/InterviewsOverview';
import ScheduleInterviewPage from '../pages/interviews/ScheduleInterviewPage';
import AssessmentsPage from '../pages/assessments/AssessmentsPage';

// Offers Pages
import OffersOverview from '../pages/offers/OffersOverview';
import CreateOfferPage from '../pages/offers/CreateOfferPage';
import OnboardingPage from '../pages/offers/OnboardingPage';

const DefaultDashboardRedirect = () => {
    const { user } = useAuthStore();
    return React.createElement(Navigate, { to: getDefaultAllowedPath(user), replace: true });
};

function AppRoutes() {
    const withAccess = (permission, element, options = {}) => React.createElement(ProtectedRoute, {
        requiresKycVerification: false,
        permission,
        superAdminOnly: Boolean(options.superAdminOnly)
    }, element);

    return React.createElement(Routes, null,
        // Public Routes
        React.createElement(Route, {
            path: '/login',
            element: React.createElement(PublicRoute, null,
                React.createElement(LoginPage)
            )
        }),

        React.createElement(Route, {
            path: '/register',
            element: React.createElement(PublicRoute, null,
                React.createElement(RegisterPage)
            )
        }),

        React.createElement(Route, {
            path: '/forgot-password',
            element: React.createElement(PublicRoute, null,
                React.createElement(ForgotPasswordPage)
            )
        }),

        React.createElement(Route, {
            path: '/reset-password',
            element: React.createElement(PublicRoute, null,
                React.createElement(ResetPasswordPage)
            )
        }),

        React.createElement(Route, {
            path: '/terms',
            element: React.createElement(LegalPage, { documentType: 'terms' })
        }),

        React.createElement(Route, {
            path: '/privacy',
            element: React.createElement(LegalPage, { documentType: 'privacy' })
        }),

        // Contact and KYC verification routes - Protected but don't require KYC completion
        React.createElement(Route, {
            path: '/contact-verification',
            element: React.createElement(ProtectedRoute, { requiresKycVerification: false },
                React.createElement(ContactVerificationPage)
            )
        }),

        React.createElement(Route, {
            path: '/kyc-verification',
            element: React.createElement(ProtectedRoute, { requiresKycVerification: false },
                React.createElement(KYCVerificationPage)
            )
        }),

        // Protected Routes - Require authentication and KYC verification for employers
        React.createElement(Route, {
                path: '/',
                element: React.createElement(ProtectedRoute, { requiresKycVerification: true },
                    React.createElement(DashboardLayout)
                )
            },
            React.createElement(Route, { index: true, element: React.createElement(DefaultDashboardRedirect) }),
            React.createElement(Route, { path: 'user-guide', element: React.createElement(EmployerGuidePage) }),
            React.createElement(Route, { path: 'dashboard', element: withAccess(EMPLOYER_PERMISSIONS.DASHBOARD, React.createElement(DashboardOverview)) }),
            React.createElement(Route, { path: 'reports', element: withAccess(EMPLOYER_PERMISSIONS.REPORTS, React.createElement(ReportsPage)) }),
            
            React.createElement(Route, { path: 'profile', element: withAccess(EMPLOYER_PERMISSIONS.PROFILE, React.createElement(ProfilePage)) }),
            
            React.createElement(Route, { path: 'config', element: withAccess(EMPLOYER_PERMISSIONS.CONFIGURATION, React.createElement(ConfigPage)) }),
            React.createElement(Route, { path: 'roles', element: withAccess(EMPLOYER_PERMISSIONS.ROLES, React.createElement(RolesPage), { superAdminOnly: true }) }),
            React.createElement(Route, { path: 'users', element: withAccess(EMPLOYER_PERMISSIONS.USERS, React.createElement(UsersPage), { superAdminOnly: true }) }),
            React.createElement(Route, { path: 'assessments', element: withAccess(EMPLOYER_PERMISSIONS.ASSESSMENTS, React.createElement(AssessmentsPage)) }),
            // Jobs Routes
            React.createElement(Route, { path: 'jobs', element: withAccess(EMPLOYER_PERMISSIONS.JOBS, React.createElement(JobsOverview)) }),
            React.createElement(Route, { path: 'jobs/create', element: withAccess(EMPLOYER_PERMISSIONS.JOBS, React.createElement(CreateJobPage)) }),
            React.createElement(Route, { path: 'jobs/:jobId/matches', element: withAccess(EMPLOYER_PERMISSIONS.CANDIDATES, React.createElement(JobMatchesPage)) }),
            React.createElement(Route, { path: 'jobs/:jobId', element: withAccess(EMPLOYER_PERMISSIONS.JOBS, React.createElement(JobDetailsPage)) }),

            // Candidates Routes
            React.createElement(Route, { path: 'candidates', element: withAccess(EMPLOYER_PERMISSIONS.CANDIDATES, React.createElement(CandidatesOverview)) }),
            React.createElement(Route, { path: 'candidates/bands', element: withAccess(EMPLOYER_PERMISSIONS.CANDIDATES, React.createElement(MatchingBands)) }),
            React.createElement(Route, { path: 'candidates/:candidateId', element: withAccess(EMPLOYER_PERMISSIONS.CANDIDATES, React.createElement(CandidateProfile)) }),

            // Interviews Routes
            React.createElement(Route, { path: 'interviews', element: withAccess(EMPLOYER_PERMISSIONS.INTERVIEWS, React.createElement(InterviewsOverview)) }),
            React.createElement(Route, { path: 'interviews/schedule', element: withAccess(EMPLOYER_PERMISSIONS.INTERVIEWS, React.createElement(ScheduleInterviewPage)) }),
            React.createElement(Route, { path: 'interviews/assessments', element: withAccess(EMPLOYER_PERMISSIONS.ASSESSMENTS, React.createElement(AssessmentsPage)) }),

            // Offers Routes
            React.createElement(Route, { path: 'offers', element: withAccess(EMPLOYER_PERMISSIONS.OFFERS, React.createElement(OffersOverview)) }),
            React.createElement(Route, { path: 'offers/create', element: withAccess(EMPLOYER_PERMISSIONS.OFFERS, React.createElement(CreateOfferPage)) }),
            React.createElement(Route, { path: 'offers/onboarding', element: withAccess(EMPLOYER_PERMISSIONS.OFFERS, React.createElement(OnboardingPage)) })
        ),

        // Catch all route
        React.createElement(Route, { path: '*', element: React.createElement(DefaultDashboardRedirect) })
    );
}

export default AppRoutes;
